import * as React from "react";
import {
  Label,
  Input,
  Textarea,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Alert,
  AlertDescription,
  Button,
} from "@repo/ui";
import { AlertTriangle, CheckCircle2, Pencil } from "lucide-react";
import { format } from "date-fns";
import { DateInput } from "@/components/forms";

export const AUDIT_DISCREPANCY_REASONS = [
  {
    value: "physical_count",
    label: "Physical Count Discrepancy",
    direction: "both" as const,
  },
  {
    value: "breakage",
    label: "Breakage/Damage",
    direction: "decrease" as const,
  },
  {
    value: "expiration",
    label: "Expiration/Disposal",
    direction: "decrease" as const,
  },
  {
    value: "theft_loss",
    label: "Theft/Loss",
    direction: "decrease" as const,
  },
  {
    value: "data_entry_error",
    label: "Data Entry Error (including initial stock corrections)",
    direction: "both" as const,
  },
  {
    value: "other",
    label: "Other (specify in justification)",
    direction: "both" as const,
  },
];

export interface AuditFormData {
  physicalCount: number;
  auditDate: string;
  reason?: string;
  justification?: string;
}

interface AuditCountFormProps {
  systemCount: number;
  onSubmit: (data: AuditFormData) => void;
  isLoading: boolean;
  submitLabel?: string;
  onCancel?: () => void;
}

export function AuditCountForm({
  systemCount,
  onSubmit,
  isLoading,
  submitLabel = "Submit Audit",
  onCancel,
}: AuditCountFormProps) {
  const [physicalCount, setPhysicalCount] = React.useState<string>("");
  const [isCountConfirmed, setIsCountConfirmed] = React.useState(false);
  const [auditDate, setAuditDate] = React.useState<string>(
    format(new Date(), "yyyy-MM-dd")
  );
  const [reason, setReason] = React.useState<string>("");
  const [justification, setJustification] = React.useState<string>("");
  const [fieldErrors, setFieldErrors] = React.useState<Record<string, string>>(
    {}
  );

  const confirmedValue = isCountConfirmed ? parseInt(physicalCount) : null;
  const quantityDifference =
    confirmedValue !== null ? confirmedValue - systemCount : 0;
  const hasDiscrepancy = confirmedValue !== null && quantityDifference !== 0;
  const countsMatch = confirmedValue !== null && quantityDifference === 0;

  const differenceDirection =
    quantityDifference > 0
      ? "increase"
      : quantityDifference < 0
        ? "decrease"
        : null;

  const filteredReasons = differenceDirection
    ? AUDIT_DISCREPANCY_REASONS.filter(
        (r) => r.direction === "both" || r.direction === differenceDirection
      )
    : AUDIT_DISCREPANCY_REASONS;

  const maxDate = format(new Date(), "yyyy-MM-dd");

  // Clear reason if it's no longer valid for the current direction
  React.useEffect(() => {
    if (reason && differenceDirection) {
      const currentReason = AUDIT_DISCREPANCY_REASONS.find(
        (r) => r.value === reason
      );
      if (
        currentReason &&
        currentReason.direction !== "both" &&
        currentReason.direction !== differenceDirection
      ) {
        setReason("");
      }
    }
  }, [differenceDirection, reason]);

  function handleConfirmCount() {
    const errors: Record<string, string> = {};
    const parsedCount = parseInt(physicalCount);

    if (!physicalCount || physicalCount.trim() === "") {
      errors.physicalCount = "Please enter the physical count";
    } else if (isNaN(parsedCount) || parsedCount < 0) {
      errors.physicalCount = "Count cannot be negative";
    }

    setFieldErrors(errors);
    if (Object.keys(errors).length > 0) return;

    setIsCountConfirmed(true);
  }

  function handleEditCount() {
    setIsCountConfirmed(false);
    setReason("");
    setJustification("");
    setFieldErrors({});
  }

  function validateAndSubmit(e: React.FormEvent) {
    e.preventDefault();

    if (!isCountConfirmed) {
      setFieldErrors({ physicalCount: "Please confirm the count first" });
      return;
    }

    const errors: Record<string, string> = {};

    if (hasDiscrepancy && !reason) {
      errors.reason = "Please select a reason for the discrepancy";
    }

    setFieldErrors(errors);
    if (Object.keys(errors).length > 0) return;

    onSubmit({
      physicalCount: confirmedValue!,
      auditDate,
      reason: hasDiscrepancy ? reason : undefined,
      justification: justification || undefined,
    });
  }

  return (
    <form onSubmit={validateAndSubmit} className="space-y-5">
      {/* Physical Count */}
      <div className="space-y-2">
        <Label htmlFor="physical-count" className="text-sm font-medium">
          How many units are physically on the shelf?{" "}
          <span className="text-destructive">*</span>
        </Label>
        <div className="flex gap-2">
          <Input
            id="physical-count"
            type="number"
            min="0"
            placeholder="Enter count"
            value={physicalCount}
            onChange={(e) => setPhysicalCount(e.target.value)}
            disabled={isCountConfirmed}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !isCountConfirmed) {
                e.preventDefault();
                handleConfirmCount();
              }
            }}
            className={`h-12 text-center text-2xl font-bold ${fieldErrors.physicalCount ? "border-destructive" : ""}`}
            data-testid="inventory-stock-qty-input"
          />
          {isCountConfirmed ? (
            <Button
              type="button"
              variant="outline"
              size="icon"
              className="h-12 w-12 shrink-0"
              onClick={handleEditCount}
            >
              <Pencil className="h-4 w-4" />
            </Button>
          ) : (
            <Button
              type="button"
              className="h-12 shrink-0"
              onClick={handleConfirmCount}
              disabled={physicalCount === ""}
            >
              Confirm
            </Button>
          )}
        </div>
        {fieldErrors.physicalCount && (
          <p className="text-sm text-destructive">
            {fieldErrors.physicalCount}
          </p>
        )}
      </div>

      {/* Result Indicator */}
      {countsMatch && (
        <Alert variant="success">
          <CheckCircle2 className="h-4 w-4" />
          <AlertDescription>
            Counts verified. No adjustment needed.
          </AlertDescription>
        </Alert>
      )}

      {hasDiscrepancy && (
        <Alert variant="warning">
          <AlertTriangle className="h-4 w-4" />
          <AlertDescription>
            Discrepancy detected. An adjustment of{" "}
            {quantityDifference > 0 ? "+" : ""}
            {quantityDifference} will be recorded. A reason is required.
          </AlertDescription>
        </Alert>
      )}

      {/* Discrepancy Reason */}
      {hasDiscrepancy && (
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="reason">
                Reason <span className="text-destructive">*</span>
              </Label>
              <Select value={reason} onValueChange={setReason}>
                <SelectTrigger
                  id="reason"
                  className={fieldErrors.reason ? "border-destructive" : ""}
                >
                  <SelectValue placeholder="Why does the count differ?" />
                </SelectTrigger>
                <SelectContent>
                  {filteredReasons.map((r) => (
                    <SelectItem key={r.value} value={r.value}>
                      {r.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {fieldErrors.reason && (
                <p className="text-sm text-destructive">{fieldErrors.reason}</p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="audit-date">
                Date <span className="text-destructive">*</span>
              </Label>
              <DateInput
                id="audit-date"
                value={auditDate}
                onChange={(v) => setAuditDate(v)}
                max={maxDate}
                className="h-10"
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="justification">
              Additional Notes{" "}
              <span className="text-muted-foreground text-xs">(optional)</span>
            </Label>
            <Textarea
              id="justification"
              placeholder="Add any additional details or explanation..."
              value={justification}
              onChange={(e) => setJustification(e.target.value)}
              rows={3}
            />
          </div>
        </div>
      )}

      {/* Audit Date when counts match */}
      {countsMatch && (
        <div className="space-y-2">
          <Label htmlFor="audit-date-match">
            Audit Date <span className="text-destructive">*</span>
          </Label>
          <DateInput
            id="audit-date-match"
            value={auditDate}
            onChange={(v) => setAuditDate(v)}
            max={maxDate}
            className="h-10 max-w-[200px]"
          />
        </div>
      )}

      {/* Actions */}
      <div className="flex justify-end gap-3 pt-4">
        {onCancel && (
          <Button type="button" variant="outline" onClick={onCancel}>
            Cancel
          </Button>
        )}
        <Button
          type="submit"
          disabled={isLoading || !isCountConfirmed}
          data-testid="inventory-save"
        >
          {submitLabel}
        </Button>
      </div>
    </form>
  );
}
